import { ref, computed, h, watch, onMounted } from 'vue'
import { SettingOutlined } from '@ant-design/icons-vue'
import { Dropdown, Menu, Checkbox } from 'ant-design-vue'

const STORAGE_KEY = 'settings-table-columns'

export function useTableColumns(initialColumns, options = {}) {
    const allColumns = ref(initialColumns)
    const settingsWidth = options.settingsWidth || 40;
    const scrollYSize = options.scrollYSize || '100vh';
    const tableKey = options.tableKey;
    const minWidth = options.width ?? false;
    const useSettingsAsHeader = options.hideSettings || false;

    const visibleColumns = computed(() => allColumns.value.filter(col => col.visible));
    const showScrollBar = ref(window.innerWidth <= 1150)

    const onResize = () => {
        showScrollBar.value = window.innerWidth <= 1150
    }

    const scrollX = computed(() => {
        if (minWidth) {
            return minWidth + settingsWidth;
        }
        const totalWidth = visibleColumns.value.reduce((sum, col) => sum + (col.width || 0), 0)
        return totalWidth + settingsWidth + 50
    })

    const scrollY = computed(() => {
        return showScrollBar.value ? undefined : `calc(${scrollYSize} - 320px)`;
    });

    const toggleColumn = (col) => {
        const visibleCount = visibleColumns.value.length
        if (col.visible && visibleCount === 1) return
        col.visible = !col.visible
    }

    const isLastVisible = (col) => col.visible && visibleColumns.value.length === 1

    const renderSettingsDropdown = (placement = 'bottomRight') => h(
        Dropdown,
        { trigger: ['click'], placement, getPopupContainer: () => document.body },
        {
            default: () => h(SettingOutlined, {
                style: { cursor: 'pointer', color: '#999' },
                onClick: (e) => e.stopPropagation(),
            }),
            overlay: () => h(
                Menu,
                { style: { maxHeight: '400px', overflowY: 'auto', minWidth: '200px' } },
                () => allColumns.value.map(col => h(
                    Menu.Item,
                    { key: col.dataIndex, disabled: isLastVisible(col) },
                    () => h(
                        Checkbox,
                        {
                            checked: col.visible,
                            onChange: () => toggleColumn(col),
                            onClick: (e) => e.stopPropagation()
                        },
                        () => col.title
                    )
                ))
            )
        }
    );

    const settingsColumn = computed(() => ({
        title: () => renderSettingsDropdown('bottomRight'),
        dataIndex: 'settings',
        width: settingsWidth,
        align: 'center',
    }))

    const columns = computed(() => {
        return useSettingsAsHeader
            ? visibleColumns.value
            : [settingsColumn.value, ...visibleColumns.value]
    });

    const resetColumns = () => {
        allColumns.value.forEach(col => col.visible = true)
    }

    onMounted(() => {
        if (!tableKey) return
        window.addEventListener('resize', onResize);

        try {
            const allSaved = localStorage.getItem(STORAGE_KEY)
            if (allSaved) {
                const parsed = JSON.parse(allSaved)
                const savedMap = parsed[tableKey]
                if (savedMap && Array.isArray(savedMap)) {
                    allColumns.value.forEach(col => {
                        const s = savedMap.find(c => c.dataIndex === col.dataIndex)
                        if (s !== undefined) col.visible = s.visible
                    })
                }
            }
        } catch (e) {
            console.warn(`Load columns error: ${tableKey}`, e)
        }
    })

    if (tableKey) {
        watch(
            () => allColumns.value.map(c => ({ dataIndex: c.dataIndex, visible: c.visible })),
            (newVal) => {
                try {
                    const allSaved = localStorage.getItem(STORAGE_KEY)
                    const parsed = allSaved ? JSON.parse(allSaved) : {}
                    parsed[tableKey] = newVal
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed))
                } catch (e) {
                    console.warn(`Save columns error: ${tableKey}`, e)
                }
            },
            { deep: true }
        )
    }

    return {
        columns,
        scrollX,
        scrollY,
        allColumns,
        toggleColumn,
        resetColumns,
        visibleColumns,
        renderSettingsDropdown
    }
}